import { useState, useRef, useEffect } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChatMessage } from "./ChatMessage";
import { ChatInput } from "./ChatInput";
import { ContactForm } from "./ContactForm";
import { v4 as uuidv4 } from 'uuid';

interface Message {
  id: string;
  content: string;
  sender?: string;
  timestamp?: string;
  isCurrentUser?: boolean;
  role?: "user" | "coordinator" | "advisor" | "interviewer";
  conversationId: string;
}

function TypingIndicator() {
  return (
    <div className="flex flex-row items-start w-full mb-2">
      <div className="flex flex-col items-start max-w-[85%] md:max-w-[78%]">
        <div className="rounded-2xl bg-gray-100 text-gray-900">
          <div className="flex space-x-1 items-center px-3 py-1.5">
            <div className="w-1.5 h-1.5 bg-gray-600 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
            <div className="w-1.5 h-1.5 bg-gray-600 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
            <div className="w-1.5 h-1.5 bg-gray-600 rounded-full animate-bounce"></div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ChatContainer() {
  console.log('ChatContainer rendering...');

  const [messages, setMessages] = useState<Message[]>([
    {
      id: "initial-greeting",
      content: "How can I help you today?",
      role: "interviewer",
      timestamp: new Date().toISOString(),
      conversationId: ""
    }
  ]);
  const [conversationId, setConversationId] = useState<string>("");
  const [currentStreamingMessage, setCurrentStreamingMessage] = useState<string>("");
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [currentRole, setCurrentRole] = useState<"user" | "coordinator" | "advisor" | "interviewer">("interviewer");
  const [showContactForm, setShowContactForm] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const webSocketRef = useRef<WebSocket | null>(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const isUserScrolling = useRef(false);
  const lastScrollPosition = useRef(0);

  useEffect(() => {
    const id = uuidv4();
    setConversationId(id);

    console.log('ChatContainer useEffect - WebSocket setup');
    const connectWebSocket = () => {
      if (!isConnecting && !webSocketRef.current) {
        setIsConnecting(true);
        // Get WebSocket URL from environment with detailed logging
        const env = (window as any).ENV || {};
        console.log('Current environment:', env);
        
        const wsUrl = env.WS_URL || (env.IS_PRODUCTION 
          ? 'wss://valeotest.medcol.io/ws/chat'
          : 'ws://localhost:8000/ws/chat');
        
        console.log('Using WebSocket URL:', wsUrl);

        try {
          const ws = new WebSocket(wsUrl);
          
          ws.onopen = () => {
            console.log('WebSocket connected');
            webSocketRef.current = ws;
            setIsConnecting(false);
          };

          ws.onmessage = (event) => {
            console.log('WebSocket message received:', event.data);
            try {
              const data = JSON.parse(event.data);
              if (data.type === 'start') {
                setIsWaitingForResponse(true);
              } else if (data.type === 'stream') {
                setCurrentStreamingMessage(data.content);
                setIsWaitingForResponse(false);
              } else if (data.type === 'end') {
                setMessages(prev => [...prev, {
                  id: Date.now().toString(),
                  content: currentStreamingMessage,
                  role: currentRole,
                  timestamp: new Date().toISOString(),
                  conversationId: conversationId
                }]);
                setCurrentStreamingMessage("");
                setIsWaitingForResponse(false);
              } else if (data.type === "error") {
                const errorMessage: Message = {
                  id: Date.now().toString(),
                  content: "Sorry, there was an error processing your message. Please try again.",
                  role: currentRole,
                  conversationId: conversationId
                };
                setMessages(prev => [...prev, errorMessage]);
                setCurrentStreamingMessage("");
              } else if (data.type === "message") {
                if (data.content.includes("TRANSITION_TO_ADVISOR")) {
                  setCurrentRole("advisor");
                  setShowContactForm(true);
                  // Don't add transition message to chat
                  return;
                }

                if (data.is_chunk) {
                  // Handle streaming chunks
                  if (data.role === 'advisor') {
                    setCurrentStreamingMessage(prev => prev + data.content);
                  } else {
                    // For non-advisor roles, add message immediately
                    const newMessage: Message = {
                      id: Date.now().toString(),
                      content: data.content,
                      role: data.role || currentRole,
                      conversationId: conversationId
                    };
                    setMessages(prev => [...prev, newMessage]);
                  }
                } else {
                  // Handle complete messages
                  if (data.role === 'advisor' && data.has_rag) {
                    // Add the complete advisor message
                    const advisorMessage: Message = {
                      id: Date.now().toString(),
                      content: data.content,
                      role: 'advisor',
                      conversationId: conversationId
                    };
                    setMessages(prev => [...prev, advisorMessage]);
                    setCurrentStreamingMessage("");
                    
                    // Show contact form after advisor's response
                    setShowContactForm(true);
                  } else {
                    // Handle regular complete messages
                    const newMessage: Message = {
                      id: Date.now().toString(),
                      content: data.content,
                      role: data.role || currentRole,
                      conversationId: conversationId
                    };
                    setMessages(prev => [...prev, newMessage]);
                    setCurrentStreamingMessage("");
                  }
                }
              }
            } catch (error) {
              console.error('Error parsing message:', error);
            }
          };

          ws.onerror = (error) => {
            console.error('WebSocket error:', error);
            setIsConnecting(false);
          };

          ws.onclose = (event) => {
            console.log('WebSocket disconnected:', {
              code: event.code,
              reason: event.reason,
              wasClean: event.wasClean
            });
            webSocketRef.current = null;
            setIsConnecting(false);
            setTimeout(connectWebSocket, 5000);
          };
        } catch (error) {
          console.error('Error connecting to WebSocket:', error);
        }
      }
    };

    connectWebSocket();

    return () => {
      if (webSocketRef.current) {
        console.log('Cleaning up WebSocket connection');
        webSocketRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    const scrollElement = scrollAreaRef.current;
    if (!scrollElement) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollElement;
      
      // Check if user is actively scrolling up
      if (scrollTop < lastScrollPosition.current) {
        isUserScrolling.current = true;
        setShouldAutoScroll(false);
      }
      
      // Only re-enable auto-scroll if user has manually scrolled to bottom
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
      if (isAtBottom && isUserScrolling.current) {
        isUserScrolling.current = false;
        setShouldAutoScroll(true);
      }
      
      lastScrollPosition.current = scrollTop;
    };

    scrollElement.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollElement.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSendMessage = async (content: string) => {
    if (!content.trim()) return;

    // Only reset scroll behavior if we're already at the bottom
    if (scrollAreaRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollAreaRef.current;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
      if (isAtBottom) {
        isUserScrolling.current = false;
        setShouldAutoScroll(true);
      }
    }

    const newMessage: Message = {
      id: Date.now().toString(),
      content: content,
      role: 'user',
      conversationId: conversationId
    };

    setMessages(prev => [...prev, newMessage]);

    try {
      if (webSocketRef.current?.readyState === WebSocket.OPEN) {
        webSocketRef.current.send(JSON.stringify(newMessage));
      } else {
        throw new Error('WebSocket not connected');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        content: "Sorry, there was an error sending your message.",
        role: "interviewer",
        conversationId: conversationId
      }]);
    }
  };

  const handleContactFormSubmit = async (formData: { name: string; email: string; phone?: string }) => {
    const env = (window as any).ENV || {};
    console.log('Current environment:', env);
    
    const submitContactUrl = env.IS_PRODUCTION 
      ? 'https://valeotest.medcol.io/submit-contact'
      : 'http://localhost:8000/submit-contact';
    
    console.log('Using WebSocket URL:', submitContactUrl);

    try {
      const response = await fetch(submitContactUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          conversation_id: conversationId,
          conversation: messages.map(msg => ({
            role: msg.role,
            content: msg.content
          }))
        }),
      });
      
      if (response.ok) {
        setShowContactForm(false);
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          content: 'Thank you for your contact information! We will get back to you soon.',
          role: "interviewer",
          conversationId: conversationId
        }]);
      } else {
        throw new Error('Failed to submit form');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        content: 'Sorry, there was an error submitting your contact information. Please try again.',
        role: "interviewer",
        conversationId: conversationId
      }]);
    }
  };

  const scrollToBottom = (behavior: 'auto' | 'smooth' = 'smooth') => {
    if (scrollAreaRef.current) {
      const scrollElement = scrollAreaRef.current;
      const lastChild = scrollElement.querySelector(':scope > div > div:last-child');
      if (lastChild) {
        lastChild.scrollIntoView({ behavior, block: 'end' });
      }
    }
  };

  useEffect(() => {
    if (!isUserScrolling.current) {
      scrollToBottom();
    }
  }, [messages]);

  useEffect(() => {
    if (currentStreamingMessage && !isUserScrolling.current) {
      scrollToBottom('auto');
    }
  }, [currentStreamingMessage]);

  const handleInputFocus = () => {
    // Небольшая задержка, чтобы дать клавиатуре время появиться
    setTimeout(() => {
      scrollToBottom('auto');
    }, 100);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-2rem)] md:h-[calc(100vh-4rem)] w-[95%] min-w-[280px] max-w-[1000px] mx-auto bg-white/80 rounded-xl overflow-hidden backdrop-blur-sm shadow-[0_0_15px_rgba(254,240,198,0.3)] border border-valeYellow/30 my-4 md:my-8">
      <ScrollArea 
        ref={scrollAreaRef} 
        className="flex-1 overflow-y-auto relative min-h-0"
        style={{ minHeight: 0 }}
      >
        <div className="space-y-4 px-3 md:px-6 py-4 md:py-6 min-w-0 w-full">
          {messages.map((message) => (
            <ChatMessage 
              key={message.id} 
              content={message.content}
              role={message.role}
            />
          ))}
          {isWaitingForResponse && <TypingIndicator />}
          {currentStreamingMessage && (
            <ChatMessage
              content={currentStreamingMessage}
              role="interviewer"
              id="streaming"
            />
          )}
          {showContactForm && (
            <div className="mt-4 px-2 md:px-4 max-w-full">
              <ContactForm 
                onSubmit={handleContactFormSubmit}
                onClose={() => setShowContactForm(false)}
              />
            </div>
          )}
        </div>
      </ScrollArea>
      <div className="px-3 md:px-6 py-4 border-t bg-white/95 backdrop-blur-sm">
        <ChatInput onSendMessage={handleSendMessage} onFocus={handleInputFocus} />
      </div>
    </div>
  );
}